/**
 * Who may shadow sections, and whose reviews count toward the per-week
 * requirement. The claim callable and the webapp's shadowing pages both decide
 * from this, so a role that the page treats as optional is never handed a
 * required claim by the server.
 */
import {
  firebaseStringToRole,
  isFirebaseRole,
  isMinimumRole,
  Role,
} from '../firebase/documents/role';

/**
 * What a course role may do in section shadowing:
 * - `required`: owes the course's required reviews, and may also take extras.
 * - `extra-only`: may shadow sections, but only as optional extra reviews that
 *   never count toward a requirement or take a credit slot.
 * - `none`: may not shadow sections.
 */
export type ShadowReviewerAccess = 'required' | 'extra-only' | 'none';

/** The lowest-ranked role that may shadow sections at all. */
const MIN_SHADOW_REVIEWER_ROLE = Role.MENTOR;

/**
 * The roles that owe required reviews. Every other role ranked at or above
 * {@link MIN_SHADOW_REVIEWER_ROLE} reviews as `extra-only`.
 */
const REQUIRED_SHADOW_REVIEWER_ROLES: ReadonlySet<Role> = new Set([
  Role.SECTION_LEADER,
  Role.SENIOR_SL,
]);

/**
 * Resolves what a course role may do in section shadowing.
 *
 * @param role - The viewer's course role. Undefined (no role in the course)
 *   resolves to `none`.
 * @return The role's shadowing access.
 * @throws Never.
 */
export function getShadowReviewerAccess(
  role: Role | undefined,
): ShadowReviewerAccess {
  if (role === undefined || !isMinimumRole(role, MIN_SHADOW_REVIEWER_ROLE)) {
    return 'none';
  }
  return REQUIRED_SHADOW_REVIEWER_ROLES.has(role) ? 'required' : 'extra-only';
}

/**
 * Resolves shadowing access from the raw `role` field of a
 * `users/{uid}/roles/{courseId}` document.
 *
 * @param roleString - The stored role value. Anything that is not a known role
 *   string, including a missing doc's undefined, resolves to `none`.
 * @return The role's shadowing access.
 * @throws Never.
 */
export function getShadowReviewerAccessForRoleString(
  roleString: unknown,
): ShadowReviewerAccess {
  if (!isFirebaseRole(roleString)) return 'none';
  return getShadowReviewerAccess(firebaseStringToRole(roleString));
}
